import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/data/content";
import { Menu, X } from "lucide-react";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (window.__lenisScrollTo) window.__lenisScrollTo(`#${id}`);
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-navy/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-16 lg:h-20">
        <button
          data-testid="nav-logo"
          onClick={() => go("thesis")}
          className="flex items-center"
        >
          <Logo size={30} />
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="relative px-3 py-2 text-sm font-body text-white/60 hover:text-white transition-colors duration-300 group"
            >
              {l.label}
              <span className="absolute bottom-1 left-3 right-3 h-px bg-flame scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <span className="font-mono text-[0.6rem] tracking-[0.25em] uppercase text-flame/70 border border-flame/30 rounded-full px-3 py-1">
            Confidential
          </span>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden bg-navy/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
        >
          <div className="px-6 py-4 grid grid-cols-2 gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left px-3 py-3 text-sm font-body text-white/70 hover:text-flame transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}
